/**
 * useSupportInbox
 * Estado da Central de Atendimento (lado do Adm): lista de conversas da empresa ativa,
 * conversa selecionada e resposta.
 *
 * @version 2025.1.0
 */
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getSocket } from '../services/socket';
import { useSupportUnreadStore } from '../stores/supportUnreadStore';
import {
  supportChatService,
  type SupportConversation,
  type SupportMessage,
} from '../services/supportChatService';

export function useSupportInbox() {
  const { enqueueSnackbar } = useSnackbar();
  const refreshUnreadCount = useSupportUnreadStore((state) => state.refresh);

  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const data = await supportChatService.listConversations();
      setConversations(data);
      setSelectedId((current) => current ?? data[0]?.id ?? null);
    } catch {
      enqueueSnackbar('Não foi possível carregar as conversas.', { variant: 'error' });
    } finally {
      setIsLoadingList(false);
    }
  }, [enqueueSnackbar]);

  const loadThread = useCallback(
    async (id: number) => {
      try {
        const { messages: msgs } = await supportChatService.getConversationMessages(id);
        setMessages(msgs);
        // Abrir a thread marca ela como lida no servidor — atualiza o balão na hora, sem
        // depender do socket (que pode perder esse aviso por corrida com a navegação).
        void refreshUnreadCount();
      } catch {
        enqueueSnackbar('Não foi possível carregar a conversa.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, refreshUnreadCount]
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId === null) {
      setMessages([]);
      return;
    }
    void loadThread(selectedId);
  }, [selectedId, loadThread]);

  const selectConversation = useCallback((id: number) => setSelectedId(id), []);

  const reply = useCallback(
    async (text: string): Promise<boolean> => {
      if (selectedId === null) return false;
      setIsSending(true);
      try {
        await supportChatService.replyToConversation(selectedId, text);
        await loadThread(selectedId);
        await loadList();
        return true;
      } catch (err) {
        const errorMessage = (err as { message?: string }).message ?? 'Não foi possível enviar a resposta.';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [selectedId, loadThread, loadList, enqueueSnackbar]
  );

  /** Aceita a conversa selecionada — a partir daqui, só quem aceitou responde. */
  const accept = useCallback(async () => {
    if (selectedId === null) return;
    setIsSending(true);
    try {
      await supportChatService.acceptConversation(selectedId);
      await loadThread(selectedId);
      await loadList();
    } catch (err) {
      const errorMessage = (err as { message?: string }).message ?? 'Não foi possível aceitar a conversa.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      // Alguém já aceitou primeiro — a lista desatualizada é o que fez o botão ainda aparecer.
      await loadList();
    } finally {
      setIsSending(false);
    }
  }, [selectedId, loadThread, loadList, enqueueSnackbar]);

  const setResolved = useCallback(
    async (resolved: boolean) => {
      if (selectedId === null) return;
      try {
        await supportChatService.updateConversationStatus(selectedId, resolved ? 'resolved' : 'open');
        await loadList();
      } catch {
        enqueueSnackbar('Não foi possível atualizar o status da conversa.', { variant: 'error' });
      }
    },
    [selectedId, loadList, enqueueSnackbar]
  );

  // Socket.IO — sala única de inbox (funciona também com o Adm em "Plataforma", sem empresa
  // ativa), com fallback de polling
  useEffect(() => {
    const socket = getSocket();

    const join = () => {
      socket.emit('join:support-inbox');
      setSocketConnected(true);
    };
    const onDisconnect = () => setSocketConnected(false);
    const onMessage = (payload: { conversationId: number; read?: boolean }) => {
      void loadList();
      // "read" é o próprio Adm marcando como lida ao abrir — recarregar a thread de novo
      // reabriria (e remarcaria) a mesma conversa, criando um ciclo com o backend.
      if (!payload.read && payload.conversationId === selectedId) {
        void loadThread(payload.conversationId);
      }
    };

    socket.on('connect', join);
    socket.on('disconnect', onDisconnect);
    socket.on('support:message', onMessage);
    if (socket.connected) join();

    return () => {
      socket.off('connect', join);
      socket.off('disconnect', onDisconnect);
      socket.off('support:message', onMessage);
      socket.emit('leave:support-inbox');
    };
  }, [selectedId, loadList, loadThread]);

  useEffect(() => {
    const interval = socketConnected ? 60000 : 10000;
    const timer = setInterval(() => void loadList(), interval);
    return () => clearInterval(timer);
  }, [socketConnected, loadList]);

  const selectedConversation = conversations.find((c) => c.id === selectedId) ?? null;
  const queue = conversations.filter((c) => c.status === 'open' && c.assignedToId === null);
  const assigned = conversations.filter((c) => !(c.status === 'open' && c.assignedToId === null));

  return {
    conversations,
    queue,
    assigned,
    selectedConversation,
    messages,
    isLoadingList,
    isSending,
    selectConversation,
    reply,
    accept,
    setResolved,
  };
}
